import { memo, useMemo } from 'react';
import {
  useNavigation,
  StackNavigationProp,
  BottomSheet,
} from '@betty/navigation';
import { ScrollView, Text } from '@betty/moonlight';
import { Payment } from '../../state/payment/payment.types';
import { getPayments } from '../Payment/Payment.utils';
import { PAYMENTS_CONFIG } from './DepositPayments.content';
import { DepositPaymentsProps } from './DepositPayments.types';
import { DepositNavigatorParamsList } from '../../navigators/DepositNavigator/DepositNavigator.types';
import usePaymentProviders from '../../hooks/usePaymentProviders';
import PaymentNotAvailable from '../PaymentNotAvailable/PaymentNotAvailable';
import PaymentComponent from '../Payment/Payment';
import DepositPaymentsSkeleton from './DepositPaymentsSkeleton';
import { useApplePay } from '../../hooks';

function DepositPayments({ amount }: DepositPaymentsProps) {
  const navigation =
    useNavigation<StackNavigationProp<DepositNavigatorParamsList>>();
  const { data: providers } = usePaymentProviders('deposits');
  const { isAvailable: isApplePayAvailable, show: showApplePay } =
    useApplePay(amount);
  const fastestMethod = useMemo(
    () =>
      isApplePayAvailable && providers?.includes('WorldpayApplePay')
        ? Payment.ApplePay
        : Payment.Visa,
    [isApplePayAvailable, providers],
  );

  const payments = useMemo(
    () => getPayments(providers, isApplePayAvailable),
    [providers, isApplePayAvailable],
  );
  const handlers = useMemo(
    () => ({
      [Payment.Visa]: () =>
        navigation.navigate('DepositLaunchScreen', { amount }),
      [Payment.Mastercard]: () =>
        navigation.navigate('DepositLaunchScreen', { amount }),
      [Payment.Interac]: () =>
        BottomSheet.open({
          namespace: 'wallet',
          type: 'DepositInterac',
          titleNS: 'wallet',
          titleKey: 'paymentInteracTitle',
          data: { amount },
          scrollable: true,
        }),
      [Payment.ApplePay]: async () => {
        const response = await showApplePay();

        if (response) {
          navigation.navigate('PaymentStatusScreen', {
            transactionId: `${response.transactionId}`,
          });
        }
      },
    }),
    [amount, navigation, showApplePay],
  );

  if (!providers) {
    return <DepositPaymentsSkeleton />;
  }

  if (!providers.length) {
    return <PaymentNotAvailable />;
  }

  return (
    <ScrollView
      contentContainerStyle={{
        gap: 'space-400',
        paddingTop: 'space-400',
        paddingHorizontal: 'space-400',
      }}
    >
      <Text
        variant="body-m"
        locNS="wallet"
        locKey="depositPaymentsTitle"
        color="darkSurfacePrimary"
      />
      {payments?.map((payment) => (
        <PaymentComponent
          key={payment}
          onPress={handlers[payment]}
          {...PAYMENTS_CONFIG[payment]}
          badgeKey={
            fastestMethod === payment ? 'paymentFastestBadge' : undefined
          }
        />
      ))}
    </ScrollView>
  );
}

export default memo(DepositPayments);
